import { useState, useCallback, useEffect } from "react";
import { trackScreenView } from "@/lib/analytics";
import { AppState, Application } from "@/lib/storage";
import { useAuth } from "@/hooks/useAuth";
import { User, Briefcase, FileText, Settings } from "lucide-react";
import ProfileTab from "./ProfileTab";
import ApplicationsTab from "./ApplicationsTab";
import CoverLetterTab from "./CoverLetterTab";
import SettingsTab from "./SettingsTab";

interface Props {
  state: AppState;
  onUpdate: (state: AppState | ((prev: AppState) => AppState)) => void;
  onEditProfile?: () => void;
  onSignUp?: () => void;
  isAuthenticated?: boolean;
}

const TABS = [
  { id: 'cover', label: 'Letter', icon: FileText },
  { id: 'applications', label: 'Jobs', icon: Briefcase },
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'settings', label: 'Settings', icon: Settings },
] as const;

type TabId = typeof TABS[number]['id'];

export default function Dashboard({ state, onUpdate, onEditProfile, onSignUp, isAuthenticated }: Props) {
  const [activeTab, setActiveTab] = useState<TabId>('cover');
  const { signOut } = useAuth();

  useEffect(() => {
    trackScreenView(activeTab);
  }, [activeTab]);

  const visibleTabs = isAuthenticated
    ? TABS
    : TABS.filter((tab) => tab.id !== 'settings');

  const handleAddApp = useCallback((app: Application) => {
    onUpdate(prev => ({ ...prev, applications: [app, ...prev.applications] }));
  }, [onUpdate]);

  const handleRemoveApp = useCallback((id: string) => {
    onUpdate(prev => ({ ...prev, applications: prev.applications.filter(a => a.id !== id) }));
  }, [onUpdate]);

  const handleUpdateStatus = useCallback((id: string, status: Application["status"]) => {
    onUpdate(prev => ({
      ...prev,
      applications: prev.applications.map(a => a.id === id ? { ...a, status } : a),
    }));
  }, [onUpdate]);

  const handleUpdateApp = useCallback((id: string, fields: Partial<Application>) => {
    onUpdate(prev => ({
      ...prev,
      applications: prev.applications.map(a => a.id === id ? { ...a, ...fields } : a),
    }));
  }, [onUpdate]);

  return (
    <div className="flex flex-col h-full">
      {/* Top tab bar - fixed below header */}
      <div className="border-b border-border bg-card px-2 py-1.5 flex justify-around shrink-0">
        {visibleTabs.map(tab => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-colors ${
                active ? 'text-primary bg-primary/5' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="text-[10px] font-medium">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {activeTab === 'profile' && (
          <ProfileTab
            profile={state.profile}
            isSignedIn={!!isAuthenticated}
            onSignInOut={signOut}
            onEditDetails={onEditProfile}
          />
        )}
        {activeTab === 'applications' && (
          <ApplicationsTab
            applications={state.applications}
            settings={state.settings}
            onAdd={handleAddApp}
            onRemove={handleRemoveApp}
            onUpdateStatus={handleUpdateStatus}
            onUpdateApp={handleUpdateApp}
          />
        )}
        {activeTab === 'cover' && (
          <CoverLetterTab
            profile={state.profile}
            savedQuestions={state.savedQuestions || []}
            onSaveQuestions={(qs) => onUpdate(prev => ({ ...prev, savedQuestions: qs }))}
            onAddApplication={handleAddApp}
            onSignUp={onSignUp}
          />
        )}
        {activeTab === 'settings' && <SettingsTab settings={state.settings} onUpdate={s => onUpdate({ ...state, settings: s })} />}
      </div>
    </div>
  );
}
